import { useTheme } from '@emotion/react';
import { Box, useMediaQuery } from '@mui/material';
import PropTypes from 'prop-types';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { TrlFlag, TrlPill } from '»components/atoms';
import { TrlOperationType } from '»components/atoms/TrlOperationType';
import TrlGridCellTrlPillAmount from '»components/atoms/TrlPills/TrlPillAmount';
import TrlGridCellTrlPillQuantity from '»components/atoms/TrlPills/TrlPillQuantity';
import { menusSlice } from '»components/Menus';
import { ordTypeList } from '»components/templates/TrlInsertOrder/TrlInsertOrderModal/TrlInsertOrderModal.helper.selectors';
import {
  useIsOrderCancellable,
  useIsOrderEditable,
  useOrdStatusLabel,
  useTrancheTypeLabel,
} from '»utils/customHooks';
import { TrlDateFormatted, TrlTimeFormatted } from '»utils/date';
import { TrlFormattedMessagePlain } from '»utils/i18n';
import { getDecimalDigits, TrlNumberFormatted } from '»utils/number';
import { getOperationLabel } from '»utils/trading/trading.utils';

import { TrlManageOrderButton } from '../TrlManageOrderButton';
import {
  DataLabelWallet,
  DataValueWallet,
  FlagContainer,
  Separator,
  StockInfo,
  StockName,
  TrancheDescr,
  Wrapper,
  WrapperButtonColumn,
  WrapperDataInfoWallet,
  WrapperDataOrderLeft,
  WrapperDataOrderText,
  WrapperFirstColumn,
  WrapperHeaderName,
  WrapperMiddleColumn,
  WrapperPills,
} from './TrlCard.styles';

export const TrlCardOrder = ({
  data,
  isStockExchangeMarket,
  isHistoric,
  openDocsModal,
  isFunds,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isBelow700 = useMediaQuery(theme.breakpoints.down('md'));

  const { getOrdStatusLabel } = useOrdStatusLabel();
  const { getTrancheTypeLabel } = useTrancheTypeLabel();

  const operationLabel = getOperationLabel(data.side);

  const isOrderEditable = useIsOrderEditable({
    revokeDate: data.revokeDate,
    ordStatus: data.ordStatus,
    securityType: data.securityType,
    side: data.side,
    localStartTime: data.localStarTtime,
    localEndTime: data.localEndTime,
  });

  const isOrderCancellable = useIsOrderCancellable({
    revokeDate: data.revokeDate,
    ordStatus: data.ordStatus,
  });

  const orderType =
    data.ordType === '1' || data.ordType === '2'
      ? ordTypeList[0].label
      : ordTypeList[1].label;

  const OrdStatusElement = getOrdStatusLabel(data.ordStatus, 'pill');

  const trancheTypeLabel = getTrancheTypeLabel(data.trancheType);

  const getStockName = () => {
    if (isStockExchangeMarket) return data.descr;
    if (isFunds) return data.securityDesc;
    return data.publicOfferId;
  };

  return (
    <Wrapper
      onClick={(e) => {
        e.stopPropagation();
        if (isStockExchangeMarket || isFunds) {
          dispatch(
            menusSlice.actions.setActiveMenuItem({
              activeMenuItem: isStockExchangeMarket
                ? 'TRADING:BOLSA:DETAILS'
                : 'TRADING:FUNDS:DETAILS',
            }),
          );
          navigate(
            isStockExchangeMarket
              ? '/Trading/StockExchange/Details'
              : '/Trading/Funds/Details',
            {
              state: {
                ordBookKey: data.ordBookKey,
                bookType: isHistoric ? 'H' : 'B',
              },
            },
          );
        }
      }}
      isOrder
      isStockExchangeMarket
      isPrimary={!isStockExchangeMarket && !isFunds}
      isHistoric={isHistoric}
    >
      <WrapperFirstColumn
        id="divOrderCardFirstColumn"
        style={{ gridArea: 'divEsq' }}
      >
        <WrapperHeaderName isStockExchangeMarket isFunds>
          <StockName>{getStockName()}</StockName>
        </WrapperHeaderName>
        {(!isStockExchangeMarket || !isFunds) && (
          <TrancheDescr>{data.trancheDescr}</TrancheDescr>
        )}
        <StockInfo isOrder>
          {isStockExchangeMarket ? (
            <FlagContainer>
              <TrlFlag flag={data.country} small={isBelow700} />
              {data.exchangeDescr}
            </FlagContainer>
          ) : (
            data.ordBookKey
          )}
          <Separator />
          {isStockExchangeMarket || isFunds ? (
            <Box>{data.symbol}</Box>
          ) : (
            <Box>{trancheTypeLabel}</Box>
          )}
          <Separator />
          <Box>{data.currency}</Box>
        </StockInfo>
        <WrapperDataOrderLeft>
          <OrdStatusElement />
          <WrapperDataOrderText>
            <WrapperDataInfoWallet>
              <DataLabelWallet>
                <FormattedMessage
                  id={
                    isStockExchangeMarket || isFunds
                      ? 'TRLT_UI-INSERTION'
                      : 'TRLT_UI-CREATION'
                  }
                />
              </DataLabelWallet>
              <DataValueWallet isOrder>
                <FormattedDate value={data.ordDate} /> |{' '}
                {isStockExchangeMarket ? (
                  <TrlTimeFormatted
                    value={data.ordDate}
                    hour="numeric"
                    minute="numeric"
                    second="numeric"
                  />
                ) : (
                  <TrlDateFormatted date={data.ordDate} option="time" />
                )}
              </DataValueWallet>
            </WrapperDataInfoWallet>
            <WrapperDataInfoWallet>
              <DataLabelWallet>
                <FormattedMessage
                  id={
                    isStockExchangeMarket || isFunds
                      ? 'TRLT_UI-EXPIRATION_DATE'
                      : 'TRLT_UI-MODIFICATION_LIMIT_SHORT'
                  }
                />
              </DataLabelWallet>
              <DataValueWallet isOrder>
                <FormattedDate
                  value={
                    isStockExchangeMarket || isFunds
                      ? data.expireTime
                      : data.revokeDate
                  }
                />
                {(!isStockExchangeMarket || !isFunds) && (
                  <>
                    {' '}
                    | <TrlDateFormatted date={data.revokeDate} option="time" />
                  </>
                )}
              </DataValueWallet>
            </WrapperDataInfoWallet>
          </WrapperDataOrderText>
        </WrapperDataOrderLeft>
      </WrapperFirstColumn>
      <WrapperMiddleColumn
        id="divOrderCardMiddleColumn"
        isStockExchangeMarket
        isFunds
        style={{ gridArea: 'divCentro' }}
      >
        <WrapperPills>
          {isStockExchangeMarket ? (
            <TrlOperationType side={data.side} label={operationLabel} />
          ) : (
            <>
              <TrlPill
                label={TrlFormattedMessagePlain({ id: 'TRLT_UI-OPEN' })}
                variant="open"
                onClick={(e) => {
                  e.stopPropagation();
                  openDocsModal(data);
                }}
              />
              {data.trancheType?.toUpperCase() === 'R' && (
                <TrlGridCellTrlPillAmount />
              )}
              {data.trancheType?.toUpperCase() === 'B' && (
                <TrlGridCellTrlPillQuantity />
              )}
            </>
          )}
        </WrapperPills>
        <WrapperDataOrderText>
          {isStockExchangeMarket && (
            <WrapperDataInfoWallet>
              <DataLabelWallet>
                {' '}
                {TrlFormattedMessagePlain({ id: 'TRLT_UI-ORDER' })}
              </DataLabelWallet>
              <DataValueWallet isOrder isUppercased>
                {TrlFormattedMessagePlain({ id: orderType })}
              </DataValueWallet>
            </WrapperDataInfoWallet>
          )}
          <WrapperDataInfoWallet>
            <DataLabelWallet>
              {' '}
              {TrlFormattedMessagePlain({ id: 'TRLT_UI-REQUESTES_QTY' })}
            </DataLabelWallet>
            <DataValueWallet isOrder>
              <TrlNumberFormatted value={data.orderQty} />
            </DataValueWallet>
          </WrapperDataInfoWallet>
          <WrapperDataInfoWallet>
            <DataLabelWallet>
              {TrlFormattedMessagePlain({
                id:
                  isStockExchangeMarket || isFunds
                    ? 'TRLT_UI-EXECUTED_SHORT'
                    : 'TRLT_UI-ASK_PRICE',
              })}
            </DataLabelWallet>
            <DataValueWallet isOrder>
              {isStockExchangeMarket || isFunds ? (
                data.cumQty
              ) : (
                <TrlNumberFormatted
                  value={data.orderPrice}
                  decimalDigits={getDecimalDigits(data.roundPrice)}
                />
              )}
            </DataValueWallet>
          </WrapperDataInfoWallet>
        </WrapperDataOrderText>
      </WrapperMiddleColumn>
      {(isStockExchangeMarket || isFunds || !isHistoric) && (
        <WrapperButtonColumn
          isStockExchangeMarket={isStockExchangeMarket}
          isFunds={isFunds}
          isPrimary={!isStockExchangeMarket && !isFunds}
          style={{ gridArea: 'divDir' }}
        >
          {isFunds ? (
            <TrlOperationType side={data.side} label={operationLabel} />
          ) : (
            <TrlManageOrderButton
              variant="edit"
              dataItem={data}
              disabled={!isOrderEditable}
              isCard
            />
          )}
          <TrlManageOrderButton
            variant="cancel"
            dataItem={data}
            disabled={!isOrderCancellable}
            isCard
          />
        </WrapperButtonColumn>
      )}
    </Wrapper>
  );
};

TrlCardOrder.propTypes = {
  data: PropTypes.object,
  isStockExchangeMarket: PropTypes.bool,
  isHistoric: PropTypes.bool,
  openDocsModal: PropTypes.func,
  isFunds: PropTypes.bool,
};
